import { useCallback, useContext, useMemo } from 'react';

import { type SessionArtifact } from '@air/server-sdk';

import {
  WorkspaceAgentPanelSessionContext,
  workspaceAgentPanelDefaultSessionValue,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentSessionArtifactUpdateMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionArtifactUpdateMutation';
import { usePrivateWorkspaceAgentSessionArtifactsList } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactsList';

const EMPTY_PINNED_ARTIFACTS: readonly SessionArtifact[] = [];
const EMPTY_PINNED_IDS: ReadonlySet<string> = new Set();

export type UsePrivateWorkspaceAgentPinnedArtifactsParams = {
  sessionId?: string | null;
  workspaceId?: string | null;
};

export const usePrivateWorkspaceAgentPinnedArtifacts = (params?: UsePrivateWorkspaceAgentPinnedArtifactsParams) => {
  const panelSession = useContext(WorkspaceAgentPanelSessionContext);
  const panelSessionId =
    panelSession === workspaceAgentPanelDefaultSessionValue ? undefined : (panelSession.sessionId ?? undefined);
  const panelWorkspaceId =
    panelSession === workspaceAgentPanelDefaultSessionValue ? undefined : panelSession.workspaceId || undefined;
  const resolvedSessionId = params?.sessionId ?? panelSessionId;
  const resolvedWorkspaceId = params?.workspaceId ?? panelWorkspaceId;

  const { data } = usePrivateWorkspaceAgentSessionArtifactsList({
    pinned: true,
    sessionId: resolvedSessionId,
    workspaceId: resolvedWorkspaceId,
  });

  const { mutate } = usePrivateWorkspaceAgentSessionArtifactUpdateMutation();

  const pinnedArtifacts = useMemo(
    () => (data?.data ?? EMPTY_PINNED_ARTIFACTS).filter((item) => item.pinned === true),
    [data?.data],
  );

  const pinnedArtifactIds = useMemo(() => {
    if (pinnedArtifacts.length === 0) {
      return EMPTY_PINNED_IDS;
    }

    return new Set(pinnedArtifacts.map((item) => item.id));
  }, [pinnedArtifacts]);

  const isArtifactPinned = useCallback(
    (artifactId: string | undefined) => (artifactId ? pinnedArtifactIds.has(artifactId) : false),
    [pinnedArtifactIds],
  );

  const setArtifactPinned = useCallback(
    (artifactId: string, pinned: boolean) => {
      if (!resolvedSessionId || !resolvedWorkspaceId) {
        return;
      }

      mutate({
        artifactId,
        pinned,
        sessionId: resolvedSessionId,
        type: 'assetList',
        workspaceId: resolvedWorkspaceId,
      });
    },
    [mutate, resolvedSessionId, resolvedWorkspaceId],
  );

  const toggleArtifactPinned = useCallback(
    (artifactId: string) => {
      setArtifactPinned(artifactId, !pinnedArtifactIds.has(artifactId));
    },
    [pinnedArtifactIds, setArtifactPinned],
  );

  return {
    canPin: Boolean(resolvedSessionId && resolvedWorkspaceId),
    isArtifactPinned,
    pinnedArtifactIds,
    pinnedArtifacts,
    sessionId: resolvedSessionId,
    setArtifactPinned,
    toggleArtifactPinned,
    workspaceId: resolvedWorkspaceId,
  };
};
